import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
/** Husky Custom Code
 * import WishListSharingStatus, {
 *     WishListSharingStatusStyles,
 * } from "@insite/content-library/Components/WishListSharingStatus";
 */
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Tag from "@insite/mobius/Icons/Tag";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

/* Husky Custom Code - Start */
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@husky-hrc/Overrides/Components/WishListSharingStatus";
import { formatDateToYYYYMMDD } from "@husky-hrc/Utilities/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Info from "@insite/mobius/Icons/Info";
import Users from "@insite/mobius/Icons/Users";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
/* Husky Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
    // Husky Custom Code
    pageLink: getPageLinkByPageType(state, "MyListsDetailsPage"),
});

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasHistory; // Husky Custom Code

export interface WishListCardStyles {
    gridContainer?: GridContainerProps;
    wishListInfoGridItem?: GridItemProps;
    wishListInfoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    extendedInfoGridContainer?: GridContainerProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    sharingStatusGridItem?: GridItemProps;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionHideClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionHideLink?: LinkPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    actionFavoriteClickable?: ClickablePresentationProps;
    basicIconGridContainer?: GridContainerProps;
    detailIconGridItem?: GridItemProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    tagTooltip?: TooltipPresentationProps;
    // Husky Custom Code
    wishListCard?: InjectableCss;
    wishListCardContent?: InjectableCss;
    wishListInfo?: InjectableCss;
    wishListActions?: InjectableCss;
    wishListActionsContent?: InjectableCss;
    wishListName?: TypographyProps;
    subText?: TypographyProps;
    viewListButton?: ButtonPresentationProps;
}

export const wishListCardStyles: WishListCardStyles = {
    gridContainer: {
        gap: 5,
    },
    wishListInfoGridItem: {
        width: [10, 10, 8, 10, 10],
        align: "middle",
    },
    basicInfoGridItem: { width: [12, 12, 5, 4, 4] },
    basicInfoGridContainer: {
        gap: 10,
        css: css`
            margin-left: 10px;
        `,
    },
    detailLinkGridItem: { width: 12 },
    descriptionGridItem: { width: 12 },
    /* Husky Custom Code - Start */
    descriptionText: {
        variant: "p",
        as: "p",
        css: css`
            overflow: hidden;
            letter-spacing: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: -webkit-box;
                            -webkit-line-clamp: 2;
                            -webkit-box-orient: vertical;
                        `,
                        null,
                        null,
                        css`
                            text-overflow: ellipsis;
                            white-space: nowrap;
                            display: inherit;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    /* Husky Custom Code - End */
    lastUpdatedGridItem: { width: 12 },
    extendedInfoGridItem: {
        width: [12, 12, 7, 8, 8],
        align: "middle",
    },
    productImageLink: {
        css: css`
            margin-right: 5px;
        `,
    },
    productImagesGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            flex-wrap: wrap;
        `,
    },
    productImage: {
        css: css`
            max-width: 75px;
            img {
                height: 100%;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    sharingStatusGridItem: { width: [12, 12, 12, 4, 4] },
    actionGridItem: {
        width: [2, 2, 4, 2, 2],
        align: "top",
        css: css`
            justify-content: flex-end;
        `,
    },
    actionGridMediumHidden: {
        css: css`
            width: 100%;
        `,
    },
    actionAddToCartButton: {
        color: "secondary",
        css: css`
            white-space: nowrap;
            width: 100%;
            padding: 0 10px;
        `, // keep text from wrapping
    },
    actionHideLink: {
        css: css`
            display: block;
            margin: 10px auto 0 auto;
        `,
    },
    actionDeleteButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    actionLeaveButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    /* Husky Custom Code - Start */
    sharingStatus: {
        privateIcon: {
            src: Users,
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                &,
                                > svg {
                                    height: 16px;
                                    width: 16px;
                                }
                            `,
                            null,
                            null,
                            css`
                                &,
                                > svg {
                                    height: 18px;
                                    width: 18px;
                                }
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        privateTooltip: {
            cssOverrides: {
                tooltipContainer: css`
                    margin-left: -80px;
                    &::after {
                        margin-left: -36px;
                    }
                `,
            },
            iconProps: {
                src: Info,
                size: 18,
                color: "common.backgroundContrast",
            },
        },
        statusText: {
            variant: "p",
            css: css`
                display: flex;
                align-items: center;
                gap: 5px;
                letter-spacing: 0px;
            `,
        },
        sharedLink: {
            typographyProps: {
                variant: "p",
            },
        },
        sharedByText: {
            variant: "p",
        },
    },
    /* Husky Custom Code - End */
    basicIconGridContainer: {
        gap: 10,
        css: css`
            width: 15px;
            flex-shrink: 0;
        `,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
    },
    tagTooltip: {
        iconProps: {
            src: Tag,
            size: 15,
            css: css`
                margin: 4px 0;
            `,
        },
    },
    /* Husky Custom Code - Start */
    wishListCard: {
        css: css`
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 300px;
                            padding: 30px 24px;
                        `,
                        null,
                        null,
                        css`
                            height: 165px;
                            padding: 30px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wishListCardContent: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            gap: 14px;
                            height: 100%;
                            justify-content: space-between;
                        `,
                        null,
                        null,
                        css`
                            flex-direction: row;
                            gap: unset;
                            height: unset;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wishListInfo: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            flex: 0 0 55%;
                            max-width: 55%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wishListActions: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            text-align: center;
                            padding-bottom: 24px;
                        `,
                        null,
                        null,
                        css`
                            margin-top: -28px;
                            flex: 0 0 45%;
                            text-align: right;
                            padding-bottom: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    wishListActionsContent: {
        css: css`
            text-align: center;
            display: inline-block;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            margin-right: 24px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    viewListButton: {
        css: css`
            height: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 278px;
                        `,
                        null,
                        null,
                        null,
                        css`
                            width: 310px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subText: {
        variant: "p",
        as: "p",
        css: css`
            letter-spacing: 0px;
        `,
    },
    wishListName: {
        variant: "h3",
        as: "h2",
        css: css`
            font-weight: bold;
            margin-bottom: 6px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            line-height: 24px;
                            letter-spacing: 0.6px;
                        `,
                        null,
                        null,
                        css`
                            line-height: 32px;
                            letter-spacing: 1px;
                            white-space: nowrap;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    actionDeleteClickable: {
        css: css`
            display: inline-block;
            color: ${getColor("danger.main")};
            font-size: 16px;
            font-weight: 600;
            line-height: 28px;
            text-align: center;
            margin-top: 10px;
            text-decoration: underline;
        `,
    },
    /* Husky Custom Code - End */
};

const WishListCard = ({
    language,
    allowEditingOfWishLists,
    isUpdatingCart,
    extendedStyles,
    wishList,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
    toggleFavorite,
    toggleHide,
    // Husky Custom Code
    history,
    pageLink,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const isLoadingAnyList = useAppSelector(state => Object.keys(state.data.wishLists.isLoading).length > 0);

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        addWishListToCart({
            apiParameter: { wishListId: wishList.id },
            onSuccess: onAddAllToCartSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("List Added to Cart"),
            messageType: "success",
        });
    };

    /* Husky Custom Code - Start */
    const onClick = (history: History, link: string | undefined) => {
        if (link) {
            history.push(link);
        }
    };
    const updatedOnDisplay = formatDateToYYYYMMDD(wishList.updatedOn);
    /* Husky Custom Code - End */
    let lastUpdatedDisplay = `${translate("Updated")} ${updatedOnDisplay}`;
    if (wishList.updatedByDisplayName && !wishList.isAutogenerated) {
        lastUpdatedDisplay += ` ${translate("by")} ${wishList.updatedByDisplayName}`;
    }

    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add List to Cart");
    const toggleHideButtonText = wishList.isHidden ? translate("Unhide List") : translate("Hide List");
    const deleteLabel = translate("Delete");
    const leaveLabel = translate("Leave List");
    const [styles] = React.useState(() => mergeToNew(wishListCardStyles, extendedStyles));
    // Husky Custom Code
    const queryString = `id=${wishList.id}`;
    const url = queryString ? `${pageLink?.url}?${queryString}` : pageLink?.url;

    return (
        <>
            {/* Husky Custom Code - Start */}
            <StyledWrapper {...styles.wishListCard} data-test-selector="wishListCard">
                <Clickable
                    {...styles.actionFavoriteClickable}
                    onClick={toggleFavorite}
                    data-test-selector={`myListsFavorite_${wishList.id}`}
                    disabled={isLoadingAnyList}
                >
                    {wishList.isFavorite && (
                        <Icon {...styles.starXIcon} data-test-selector={`myListsFavoriteStarXIcon_${wishList.id}`} />
                    )}
                    {!wishList.isFavorite && (
                        <Icon {...styles.starIcon} data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`} />
                    )}
                </Clickable>
                <Typography {...styles.wishListName}>{wishList.name}</Typography>
                <StyledWrapper {...styles.wishListCardContent}>
                    <StyledWrapper {...styles.wishListInfo}>
                        {wishList.description && (
                            <Typography {...styles.descriptionText} data-test-selector="wishListCardDescription">
                                {wishList.description}
                            </Typography>
                        )}
                        <Typography {...styles.subText} data-test-selector="wishListCardLastUpdated">
                            {lastUpdatedDisplay}
                        </Typography>
                        <WishListSharingStatus extendedStyles={styles.sharingStatus} wishList={wishList} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.wishListActions}>
                        <StyledWrapper {...styles.wishListActionsContent}>
                            <Button
                                data-test-selector="wishListCardName"
                                variant="primary"
                                {...styles.viewListButton}
                                onClick={() => onClick(history, url)}
                            >
                                {translate("View List")}
                            </Button>
                            {allowEditingOfWishLists && !!deleteWishList && !wishList.isSharedList && (
                                <StyledWrapper>
                                    <Clickable
                                        data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                                        {...styles.actionDeleteClickable}
                                        onClick={deleteWishList}
                                    >
                                        {deleteLabel}
                                    </Clickable>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {/* Husky Custom Code - End */}
        </>
    );
};

export default connect(mapStateToProps)(withHistory(WishListCard));
